import { supabase } from '../supabase/client';

export interface CommissionConfig {
  id: string;
  versionNumber: string;
  isActive: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  baseCommissionRate: number;
  /** 統包（company_fixed）非 Premium 之平台佣金率 */
  companyFixedBaseCommissionRate: number;
  /** 與 DB `premium_commission_rate` 同步，等同個人工匠 Premium 預設率 */
  premiumCommissionRate: number;
  premiumCraftsmanCommissionRate: number;
  premiumCompanyCommissionRate: number;
  matchingFee: number;
  emergencyFeeMultiplier: number;
  videoConsultationFee: number;
  warrantyFee: number;
  advancedConfig: Record<string, any>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  notes: string | null;
}

export interface CommissionConfigAuditLog {
  id: string;
  configId: string;
  action: 'created' | 'updated' | 'activated' | 'deactivated' | 'deleted';
  performedBy: string | null;
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
  notes: string | null;
  createdAt: string;
}

export interface CreateCommissionConfigPayload {
  versionNumber: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  baseCommissionRate: number;
  companyFixedBaseCommissionRate: number;
  premiumCraftsmanCommissionRate: number;
  premiumCompanyCommissionRate: number;
  matchingFee: number;
  emergencyFeeMultiplier: number;
  videoConsultationFee: number;
  warrantyFee: number;
  advancedConfig?: Record<string, any>;
  notes?: string | null;
}

const mapConfig = (row: any): CommissionConfig => {
  const legacyPremium = Number(row.premium_commission_rate);
  const craftsmanPrem =
    row.premium_craftsman_commission_rate != null
      ? Number(row.premium_craftsman_commission_rate)
      : legacyPremium;
  const companyPrem =
    row.premium_company_commission_rate != null
      ? Number(row.premium_company_commission_rate)
      : legacyPremium;
  return {
    id: row.id,
    versionNumber: row.version_number,
    isActive: row.is_active,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
    baseCommissionRate: Number(row.base_commission_rate),
    companyFixedBaseCommissionRate: Number(
      row.company_fixed_base_commission_rate != null
        ? row.company_fixed_base_commission_rate
        : 0.04
    ),
    premiumCommissionRate: craftsmanPrem,
    premiumCraftsmanCommissionRate: craftsmanPrem,
    premiumCompanyCommissionRate: companyPrem,
    matchingFee: Number(row.matching_fee),
    emergencyFeeMultiplier: Number(row.emergency_fee_multiplier),
    videoConsultationFee: Number(row.video_consultation_fee),
    warrantyFee: Number(row.warranty_fee),
    advancedConfig: row.advanced_config ?? {},
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    notes: row.notes,
  };
};

const mapAuditLog = (row: any): CommissionConfigAuditLog => ({
  id: row.id,
  configId: row.config_id,
  action: row.action,
  performedBy: row.performed_by,
  oldValues: row.old_values,
  newValues: row.new_values,
  notes: row.notes,
  createdAt: row.created_at,
});

export class CommissionConfigService {
  static async getActiveConfig(): Promise<{
    config: CommissionConfig | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('commission_config')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        return { config: null, error: error.message };
      }

      return { config: data ? mapConfig(data) : null, error: null };
    } catch (error) {
      console.error('Failed to load active commission config', error);
      return { config: null, error: '無法載入抽成設定' };
    }
  }

  static async getAllConfigs(): Promise<{
    configs: CommissionConfig[];
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('commission_config')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        return { configs: [], error: error.message };
      }

      return { configs: (data || []).map(mapConfig), error: null };
    } catch (error) {
      console.error('Failed to load commission configs', error);
      return { configs: [], error: '無法載入抽成設定列表' };
    }
  }

  static async getConfigById(id: string): Promise<{
    config: CommissionConfig | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('commission_config')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        return { config: null, error: error.message };
      }

      return { config: data ? mapConfig(data) : null, error: null };
    } catch (error) {
      console.error('Failed to fetch commission config', error);
      return { config: null, error: '無法查詢抽成設定' };
    }
  }

  static async createConfig(
    payload: CreateCommissionConfigPayload,
    userId: string
  ): Promise<{
    config: CommissionConfig | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('commission_config')
        .insert({
          version_number: payload.versionNumber,
          effective_from: payload.effectiveFrom ?? null,
          effective_to: payload.effectiveTo ?? null,
          base_commission_rate: payload.baseCommissionRate,
          company_fixed_base_commission_rate: payload.companyFixedBaseCommissionRate,
          premium_commission_rate: payload.premiumCraftsmanCommissionRate,
          premium_craftsman_commission_rate: payload.premiumCraftsmanCommissionRate,
          premium_company_commission_rate: payload.premiumCompanyCommissionRate,
          matching_fee: payload.matchingFee,
          emergency_fee_multiplier: payload.emergencyFeeMultiplier,
          video_consultation_fee: payload.videoConsultationFee,
          warranty_fee: payload.warrantyFee,
          advanced_config: payload.advancedConfig ?? {},
          notes: payload.notes ?? null,
          created_by: userId,
          is_active: false,
        })
        .select('*')
        .single();

      if (error) {
        return { config: null, error: error.message };
      }

      // 記錄審計日誌
      await supabase.from('commission_config_audit_logs').insert({
        config_id: data.id,
        action: 'created',
        performed_by: userId,
        new_values: data,
        notes: `建立新版本：${payload.versionNumber}`,
      });

      return { config: data ? mapConfig(data) : null, error: null };
    } catch (error) {
      console.error('Failed to create commission config', error);
      return { config: null, error: '新增抽成設定失敗' };
    }
  }

  static async activateConfig(id: string, userId: string): Promise<string | null> {
    try {
      // 先取得舊設定
      const { data: oldConfig } = await supabase
        .from('commission_config')
        .select('*')
        .eq('id', id)
        .single();

      // 更新為 active（trigger 會自動將其他設定設為 inactive）
      const { error } = await supabase
        .from('commission_config')
        .update({
          is_active: true,
          effective_from: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        return error.message;
      }

      // 記錄審計日誌
      await supabase.from('commission_config_audit_logs').insert({
        config_id: id,
        action: 'activated',
        performed_by: userId,
        old_values: oldConfig,
        new_values: { is_active: true },
        notes: '啟用此版本設定',
      });

      return null;
    } catch (error) {
      console.error('Failed to activate commission config', error);
      return '啟用抽成設定失敗';
    }
  }

  static async getAuditLogs(configId: string): Promise<{
    logs: CommissionConfigAuditLog[];
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('commission_config_audit_logs')
        .select('*')
        .eq('config_id', configId)
        .order('created_at', { ascending: false });

      if (error) {
        return { logs: [], error: error.message };
      }

      return { logs: (data || []).map(mapAuditLog), error: null };
    } catch (error) {
      console.error('Failed to load audit logs', error);
      return { logs: [], error: '無法取得審計日誌' };
    }
  }
}


